import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';

interface GeneratedCode {
  code: string;
  creditsGB: number;
  creditsMB: number;
  creditsBytes: number;
  expiresAt: string | null;
}

interface User {
  id: number;
  email: string;
  name: string;
  authType: string;
  emailVerified: boolean;
  creditsBytes: number;
  creditsGB: string;
  totalUsageBytes: number;
  totalUsageGB: string;
  createdAt: string;
  proxyUserId: string;
}

interface GlobalUsageStat {
  usage_type: string;
  total_bytes: number;
  total_gb: number;
  total_sessions: number;
  last_updated: string;
}

const AdminPanel: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [codeForm, setCodeForm] = useState({ creditsGB: 1, quantity: 1, expiresAt: '' });
  const [generatedCodes, setGeneratedCodes] = useState<GeneratedCode[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalUsageStat[]>([]);
  const [mobileProxyStats, setMobileProxyStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'codes' | 'users' | 'mobile'>('codes');
  const [statsHours, setStatsHours] = useState(24);

  // Handle admin login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await authAPI.adminLogin(loginForm.username, loginForm.password);
      if (response.success) {
        setIsLoggedIn(true);
        sessionStorage.setItem('admin_logged_in', 'true');
        // Load data immediately after login
        await loadUsers();
        await loadGlobalStats();
        await loadMobileProxyStats();
      } else {
        setError('Invalid credentials');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Load all users
  const loadUsers = async () => {
    try {
      const response = await authAPI.getAllUsers();
      if (response.success) {
        setUsers(response.users);
      } else {
        setError('Failed to load users');
      }
    } catch (err: any) {
      setError('Failed to load users');
    }
  };

  // Load global usage statistics
  const loadGlobalStats = async () => {
    try {
      const response = await authAPI.getGlobalUsageStats();
      setGlobalStats(response.stats || []);
    } catch (err: any) {
      console.error('Failed to load global stats:', err);
      setGlobalStats([]); // Ensure it's always an array
    }
  };

  // Load mobile proxy statistics
  const loadMobileProxyStats = async () => {
    try {
      const response = await authAPI.getMobileProxyStats(statsHours);
      setMobileProxyStats(response);
    } catch (err: any) {
      console.error('Failed to load mobile proxy stats:', err);
      setMobileProxyStats(null);
    }
  };

  // Handle code generation
  const handleGenerateCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        creditsGB: parseFloat(codeForm.creditsGB.toString()),
        quantity: parseInt(codeForm.quantity.toString()),
        expiresAt: codeForm.expiresAt || null
      };
      
      const response = await authAPI.generateCodes(payload);
      setGeneratedCodes(response.codes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };

  // Check if already logged in
  useEffect(() => {
    const adminLoggedIn = sessionStorage.getItem('admin_logged_in');
    if (adminLoggedIn === 'true') {
      setIsLoggedIn(true);
      loadUsers();
      loadGlobalStats();
    }
  }, []);

  // Logout function
  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('admin_logged_in');
    setGeneratedCodes([]);
    setUsers([]);
  };

  // Login form (unchanged)
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-md">
          <h1 className="text-2xl font-bold text-white text-center mb-6">Admin Panel</h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                required
              />
            </div>

            {error && (
              <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition duration-200"
          >
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8">
          <button
            onClick={() => setActiveTab('codes')}
            className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
              activeTab === 'codes' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Generate Codes
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
              activeTab === 'users' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('mobile');
              loadMobileProxyStats();
            }}
            className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
              activeTab === 'mobile' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Mobile Proxies
          </button>
        </div>

        {/* Code Generation Tab */}
        {activeTab === 'codes' && (
          <>
            {/* Code Generation Form */}
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Generate Top-Up Codes</h2>
              
              <form onSubmit={handleGenerateCodes} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Credits (GB)</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={codeForm.creditsGB}
                      onChange={(e) => setCodeForm({...codeForm, creditsGB: parseFloat(e.target.value)})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={codeForm.quantity}
                      onChange={(e) => setCodeForm({...codeForm, quantity: parseInt(e.target.value)})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Expires At (Optional)</label>
                    <input
                      type="datetime-local"
                      value={codeForm.expiresAt}
                      onChange={(e) => setCodeForm({...codeForm, expiresAt: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-2 px-6 rounded-lg transition duration-200"
                >
                  {loading ? 'Generating...' : 'Generate Codes'}
                </button>
              </form>
            </div>

            {/* Generated Codes */}
            {generatedCodes.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-bold text-white mb-4">Generated Codes</h2>
                
                <div className="space-y-3">
                  {generatedCodes.map((codeData, index) => (
                    <div key={index} className="bg-gray-700 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-xl font-mono text-green-400">{codeData.code}</span>
                          <div className="text-sm text-gray-300 mt-1">
                            {codeData.creditsGB}GB ({codeData.creditsMB}MB, {codeData.creditsBytes.toLocaleString()} bytes)
                            {codeData.expiresAt && (
                              <span className="ml-4">Expires: {new Date(codeData.expiresAt).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => navigator.clipboard.writeText(codeData.code)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">All Users</h2>
                <button
                  onClick={() => { loadUsers(); loadGlobalStats(); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                >
                  Refresh All
                </button>
              </div>
            </div>
            
            {/* Summary Statistics */}
            {users.length > 0 && (
              <div className="px-6 py-4 bg-gray-700 border-b border-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Total Users</p>
                    <p className="text-2xl font-bold text-white">{users.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Total Credits Available</p>
                    <p className="text-2xl font-bold text-green-400">
                      {users.reduce((sum, user) => sum + parseFloat(user.creditsGB), 0).toFixed(2)} GB
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Total Usage</p>
                    <p className="text-2xl font-bold text-blue-400">
                      {globalStats && globalStats.length > 0 
                        ? globalStats.reduce((sum, stat) => sum + Number(stat.total_gb), 0).toFixed(3)
                        : users.reduce((sum, user) => sum + parseFloat(user.totalUsageGB), 0).toFixed(2)
                      } GB
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Total Data Purchased</p>
                    <p className="text-2xl font-bold text-purple-400">
                      {(users.reduce((sum, user) => sum + parseFloat(user.creditsGB), 0) + 
                        users.reduce((sum, user) => sum + parseFloat(user.totalUsageGB), 0)).toFixed(2)} GB
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Global Usage Statistics */}
            {globalStats && globalStats.length > 0 && (
              <div className="px-6 py-4 bg-gray-800 border-b border-gray-600">
                <h3 className="text-lg font-bold text-white mb-4">📊 Global Proxy Usage Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {globalStats.map((stat) => {
                    const typeNames = {
                      'normal_proxy': 'Normal Proxies',
                      'shop_proxy_regular': 'Shop Proxies (Regular)',
                      'shop_proxy_admin': 'Shop Proxies (Admin)'
                    };
                    const typeColors = {
                      'normal_proxy': 'text-blue-400',
                      'shop_proxy_regular': 'text-green-400',
                      'shop_proxy_admin': 'text-purple-400'
                    };
                    
                    return (
                      <div key={stat.usage_type} className="text-center bg-gray-700 rounded-lg p-4">
                        <p className="text-sm text-gray-400">{typeNames[stat.usage_type as keyof typeof typeNames]}</p>
                        <p className={`text-xl font-bold ${typeColors[stat.usage_type as keyof typeof typeColors]}`}>
                          {Number(stat.total_gb).toFixed(3)} GB
                        </p>
                        <p className="text-xs text-gray-500">{stat.total_sessions} sessions</p>
                        <p className="text-xs text-gray-500">Updated: {new Date(stat.last_updated).toLocaleTimeString()}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Credits (GB)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Total Usage (GB)</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Auth Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Verified</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-white">{user.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{user.name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`${parseFloat(user.creditsGB) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {user.creditsGB}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-blue-400">{user.totalUsageGB}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.authType === 'google' ? 'bg-red-900 text-red-200' : 'bg-blue-900 text-blue-200'
                        }`}>
                          {user.authType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.emailVerified ? 'bg-green-900 text-green-200' : 'bg-yellow-900 text-yellow-200'
                        }`}>
                          {user.emailVerified ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  
                  {/* Summary Row */}
                  {users.length > 0 && (
                    <tr className="bg-gray-600 border-t-2 border-blue-500 font-semibold">
                      <td className="px-4 py-3 text-sm text-white font-bold">TOTAL ({users.length} users)</td>
                      <td className="px-4 py-3 text-sm text-gray-300">-</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-green-400 font-bold">
                          {users.reduce((sum, user) => sum + parseFloat(user.creditsGB), 0).toFixed(2)} GB
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-400 font-bold">
                          {users.reduce((sum, user) => sum + parseFloat(user.totalUsageGB), 0).toFixed(2)} GB
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">-</td>
                      <td className="px-4 py-3 text-sm text-gray-300">-</td>
                      <td className="px-4 py-3 text-sm text-gray-300">-</td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  No users found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Proxy Statistics Tab */}
        {activeTab === 'mobile' && (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Mobile Proxy Hourly Usage</h2>
              <div className="flex items-center space-x-4">
                <select
                  value={statsHours}
                  onChange={(e) => {
                    setStatsHours(parseInt(e.target.value));
                    setTimeout(() => loadMobileProxyStats(), 100);
                  }}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="24">Last 24 Hours</option>
                  <option value="48">Last 48 Hours</option>
                  <option value="168">Last 7 Days</option>
                </select>
                <button
                  onClick={loadMobileProxyStats}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                >
                  Refresh
                </button>
              </div>
            </div>

            {!mobileProxyStats ? (
              <div className="text-center py-12 text-gray-400">
                <p>Loading mobile proxy statistics...</p>
              </div>
            ) : (
              <>
                {/* Total Usage Per Proxy */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4">📊 Total Usage Per Proxy</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {mobileProxyStats.proxyTotals?.map((proxy: any) => (
                      <div key={proxy.proxy_name} className="bg-gray-700 p-4 rounded-lg">
                        <div className="text-sm text-gray-400">{proxy.proxy_name}</div>
                        <div className="text-2xl font-bold text-blue-400 mt-1">{proxy.total_gb} GB</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {proxy.total_unique_users} users • {proxy.hour_count} hours tracked
                        </div>
                      </div>
                    ))}
                    {(!mobileProxyStats.proxyTotals || mobileProxyStats.proxyTotals.length === 0) && (
                      <div className="col-span-3 text-center py-4 text-gray-400">
                        No data yet - tracking will start once mobile proxies are used
                      </div>
                    )}
                  </div>
                </div>

                {/* Peak Hours Chart */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">⏰ Peak Hours (All Proxies)</h3>
                  <div className="space-y-2">
                    {mobileProxyStats.peakHours?.slice(0, 12).map((hour: any) => {
                      const maxGb = Math.max(...(mobileProxyStats.peakHours?.map((h: any) => parseFloat(h.total_gb)) || [1]));
                      const widthPercent = (parseFloat(hour.total_gb) / maxGb) * 100;
                      
                      return (
                        <div key={hour.hour} className="flex items-center space-x-3">
                          <div className="w-32 text-sm text-gray-400 flex-shrink-0">
                            {new Date(hour.hour).toLocaleDateString()} {new Date(hour.hour).getHours()}:00
                          </div>
                          <div className="flex-1 bg-gray-700 rounded-full h-8 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-full flex items-center px-3"
                              style={{ width: `${Math.max(widthPercent, 5)}%` }}
                            >
                              <span className="text-xs font-semibold text-white">{hour.total_gb} GB</span>
                            </div>
                          </div>
                          <div className="w-20 text-sm text-gray-400 text-right">
                            {hour.proxy_count} proxies
                          </div>
                        </div>
                      );
                    })}
                    {(!mobileProxyStats.peakHours || mobileProxyStats.peakHours.length === 0) && (
                      <div className="text-center py-8 text-gray-400">
                        No hourly data yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Detailed Hourly Breakdown */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-white mb-4">📅 Detailed Hourly Breakdown</h3>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-700 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Proxy Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Hour</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Data (GB)</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Users</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {mobileProxyStats.hourlyStats?.map((stat: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm text-white font-mono">{stat.proxy_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {new Date(stat.hour).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-400 text-right font-semibold">
                              {stat.gb_transferred}
                            </td>
                            <td className="px-4 py-3 text-sm text-green-400 text-right font-semibold">
                              {stat.unique_users}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!mobileProxyStats.hourlyStats || mobileProxyStats.hourlyStats.length === 0) && (
                      <div className="text-center py-8 text-gray-400">
                        No hourly data yet - tracking will begin once mobile proxies are used
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 